import axios from 'axios';
import { Currency } from 'src/@types/currency';
import { TransactionsData, TransactionsReturnData } from 'src/@types/transactions';
import { GameData, GameEdit, GameReturnData } from 'src/@types/games';
import { LogInData, AuthenticateData, User, UserEdit } from 'src/@types/marketplaceUser';
import { ProviderReturnData, ProviderData, ProviderEditData } from 'src/@types/provider';
import { Entity, GetUsersData } from 'src/@types/users';
import { API_URL } from 'src/config';
import { CategoryEditData, CategoryReturnData, CategoryCreateData } from 'src/@types/category';
import { BetData, BetReturnData } from 'src/@types/bets';
import { SummaryRequestData, SummaryReturnData } from 'src/@types/summary';
import { UpdateMarketplaceData } from 'src/redux/slices/marketplace';
import { MarketplaceEditValues } from 'src/@types/marketplace';
import { Banner } from 'src/@types/banner';
import {
  UserStatisticsData,
  UserStatisticsReturnData,
  RevenueData,
  RevenueStatisticsReturnData,
} from 'src/@types/statistics';
import { FrbData } from 'src/@types/frb';

const createConfig = (formData = false): any => {
  const url = window.location !== window.parent.location ? document.referrer : document.location.href;
  const token = window?.localStorage?.getItem('accessToken')?.replace(/["']/g, '');
  return {
    headers: {
      authorization: `Bearer ${token && token}`,
      Source: url,
      'Access-Control-Allow-Origin': '*',
      'Content-Type': formData ? 'multipart/form-data' : 'application/json',
    },
  };
};

type ApiErrorPayload = {
  message?: string;
  error?: string;
  errorMessage?: string | { message?: string };
  errors?: Array<string | { message?: string; error?: string }>;
};

export const getApiErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const errorData = error.response?.data as ApiErrorPayload | undefined;

    if (typeof errorData?.errorMessage === 'string') {
      return errorData.errorMessage;
    }

    if (typeof errorData?.errorMessage === 'object' && errorData.errorMessage?.message) {
      return errorData.errorMessage.message;
    }

    if (Array.isArray(errorData?.errors) && errorData.errors.length > 0) {
      return errorData.errors
        .map((item) => (typeof item === 'string' ? item : item.message || item.error))
        .filter(Boolean)
        .join(', ');
    }

    if (errorData?.message) {
      return errorData.message;
    }

    if (errorData?.error) {
      return errorData.error;
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (typeof error === 'string' && error) {
    return error;
  }

  return 'Something went wrong';
};

export const get = async (
  route: string
): Promise<ProviderReturnData | CategoryReturnData | Currency[] | Entity[] | string[] | Banner[]> => {
  const config = createConfig();
  try {
    const returnData = await axios.get(API_URL + route, config);
    return returnData?.data;
  } catch (error) {
    console.error(error);
    throw new Error(getApiErrorMessage(error));
  }
};

export const post = async (
  route: string,
  data:
    | GameData
    | LogInData
    | AuthenticateData
    | GetUsersData
    | TransactionsData
    | CategoryCreateData
    | ProviderData
    | SummaryRequestData
    | UserStatisticsData
    | BetData
    | RevenueData
    | FrbData,
  formData = false
): Promise<
  | GameReturnData
  | User
  | User[]
  | TransactionsReturnData
  | ProviderReturnData
  | SummaryReturnData
  | UserStatisticsReturnData
  | BetReturnData
  | RevenueStatisticsReturnData
> => {
  const config = createConfig(formData);
  try {
    const returnData = await axios.post(API_URL + route, data, config);
    return returnData?.data;
  } catch (error) {
    console.error(error);
    throw new Error(getApiErrorMessage(error));
  }
};

export const patch = async (
  route: string,
  data: MarketplaceEditValues | GameEdit | CategoryEditData | ProviderEditData | UserEdit,
  formData = false
): Promise<UpdateMarketplaceData> => {
  const config = createConfig(formData);
  try {
    const returnData = await axios.patch(API_URL + route, data, config);
    return returnData?.data;
  } catch (error) {
    console.error(error);
    throw new Error(getApiErrorMessage(error));
  }
};

export const axiosDelete = async (route: string): Promise<Banner> => {
  const config = createConfig();
  try {
    const returnData = await axios.delete(API_URL + route, config);
    return returnData?.data;
  } catch (error) {
    console.error(error);
    throw new Error(getApiErrorMessage(error));
  }
};
